import * as Sentry from '@sentry/react'
import posthog from 'posthog-js'

import { isIgnoredException } from './ignored-errors'
import { trackRouteChanges } from './posthog-route-pageview'
import type { RouteEventSource } from './posthog-route-pageview'

const apiKey = import.meta.env.VITE_POSTHOG_KEY as string | undefined
// Default to a same-origin reverse proxy under a random-looking prefix so
// adblockers don't drop PostHog requests by path. Caddy proxies the prefix
// to us.i.posthog.com (`/static/*` → us-assets.i.posthog.com). Rotate the
// suffix here AND in infra/caddy/observability-tunnel.caddy together if
// filterlists ever start catching it. Use `||` (not `??`) so an empty
// VITE_POSTHOG_HOST from CI also falls back to the default.
const host =
    (import.meta.env.VITE_POSTHOG_HOST as string | undefined)?.trim() ||
    '/relay-2hd9'

let initialized = false
let analyticsActor: { actor: string; impersonating: boolean } | null = null

export function getAnalyticsActor() {
    return analyticsActor ? { ...analyticsActor } : null
}

function sanitizeWorkflowProperties(
    properties: Record<string, unknown>,
): Record<string, unknown> {
    return Object.fromEntries(
        Object.entries(properties).map(([key, value]) => {
            if (
                (key === '$set' || key === '$set_once') &&
                value &&
                typeof value === 'object'
            ) {
                return [
                    key,
                    sanitizeWorkflowProperties(
                        value as Record<string, unknown>,
                    ),
                ]
            }
            if (key === '$search' || key === '$query' || key === '$hash')
                return [key, '']
            if (/url|referrer/i.test(key) && typeof value === 'string') {
                return [key, value.split(/[?#]/, 1)[0]]
            }
            return [key, value]
        }),
    )
}

const PII_NETWORK_PATHS = [
    '/customers',
    '/sales-orders',
    '/payments',
    '/einvoice',
    '/api/', // Authenticated ERP payloads may contain names, notes or financial values.
    '/expenses',
    '/tea-purchases',
    '/dried-tea-purchases',
    '/delivery-trips',
    '/stocktake',
    '/grower',
    '/remittances',
    '/contract',
    '/weighings',
    '/care-logs',
    '/agricultural-materials',
    '/agri-material',
    '/sorting-lots',
    '/scenting-lots',
    '/packaging',
    '/stock-in',
    '/stock-out',
    '/product-stock',
]

/**
 * Which business events belong here — and which do not.
 *
 * The database is the only source for "how many": it records every path, an ad
 * blocker cannot drop rows from it, and the reporting layer already answers
 * order, cash-flow and debt questions from it.
 *
 * Browser events answer a different question — what happened inside the
 * browser, which the database never sees: failed requests, session replay, how
 * long a screen took to become usable.
 *
 * A business event captured in the browser will only ever cover the paths that
 * go through that one component. Measured on production: payment_recorded saw 8
 * of 116 real collections (six code paths create one, one of them emitted the
 * event) and order_delivered saw 97 of 253. Charts built on those numbers were
 * quietly wrong, which is worse than having no chart. They were removed rather
 * than completed.
 *
 * `sales_order_created` is the deliberate exception and is kept for ONE reason:
 * it has a single entry path, so its count matches the database exactly, which
 * makes it the reference point the daily drift check uses to tell whether events
 * are still arriving at all. It is not there to count sales.
 *
 * Before adding a business event, ask what it answers that the database cannot.
 * If the answer is "nothing", the report belongs in the reporting layer instead.
 */
export function initPostHog() {
    if (initialized) return
    if (!apiKey) {
        // Chỉ nhắc ở máy dev; bản build không key (staging) im lặng (ERP-12).
        if (import.meta.env.DEV)
            console.warn(
                '[PostHog] VITE_POSTHOG_KEY missing — analytics disabled',
            )
        return
    }

    posthog.init(apiKey, {
        api_host: host,
        // Keep toolbar / replay links pointing at the real PostHog UI, even
        // though ingestion is tunneled through the same-origin proxy.
        ui_host: 'https://us.posthog.com',
        autocapture: false,
        // Runs after SDK enrichment, including persisted initial URL metadata.
        before_send: (event) => {
            if (event && isIgnoredException(event)) return null
            if (event)
                event.properties = sanitizeWorkflowProperties(event.properties)
            return event
        },
        capture_pageview: false,
        capture_pageleave: false,
        disable_session_recording: false,
        // Error handlers log whole error objects, which can carry request
        // payloads with customer data.
        enable_recording_console_log: false,
        // Replays show the screen as the user saw it so the team can check
        // real data; only password fields stay hidden.
        session_recording: {
            maskAllInputs: false,
            maskInputOptions: { password: true },
            maskCapturedNetworkRequestFn: (request) => {
                if (PII_NETWORK_PATHS.some((p) => request.name.includes(p))) {
                    request.requestBody = null
                    request.responseBody = null
                }
                request.name = request.name.split(/[?#]/, 1)[0]
                return request
            },
            recordCrossOriginIframes: false,
        },
    })
    // Stamped on every event from this build. `tenant` is set now, while there
    // is only one deployment, because events already sent cannot be relabelled
    // later — without it the day a second tenant exists there is no way to split
    // the history that came before.
    posthog.register(
        buildSuperProperties({
            mode: import.meta.env.MODE,
            appVersion: __APP_VERSION__,
            tenantSlug: import.meta.env.VITE_TENANT_SLUG as string | undefined,
        }),
    )

    initialized = true
    console.info(`[PostHog] initialized → ${host}`)
}

/** Properties attached to every event this build sends. */
export interface SuperProperties {
    env: string
    app_version: string
    tenant: string
}

// The only deployment today. Named rather than left empty so events are never
// labelled "undefined", which would be indistinguishable from a bug later.
const DEFAULT_TENANT = 'haodat'

export function buildSuperProperties(input: {
    mode: string
    appVersion: string
    tenantSlug: string | undefined
}): SuperProperties {
    return {
        env: input.mode,
        app_version: input.appVersion,
        tenant: input.tenantSlug?.trim() || DEFAULT_TENANT,
    }
}

/**
 * Properties recorded against the identified person.
 *
 * `impersonating` exists because the identity deliberately stays the ACTOR while
 * they act as somebody else. That is the right call — the target's identity must
 * not be polluted — but it makes an impersonated session look exactly like the
 * actor doing their own work. The flag is what tells the two apart afterwards.
 */
export function identityProperties(
    user: { id: string; username: string },
    impersonating: boolean,
): { username: string; impersonating: boolean } {
    return { username: user.username, impersonating }
}

export function identifyUser(
    user: { id: string; username: string },
    impersonating = false,
) {
    analyticsActor = { actor: user.id, impersonating }
    if (initialized) {
        posthog.identify(user.id, identityProperties(user, impersonating))
    }
    // Sentry.setUser is a no-op when Sentry isn't initialized, so it's safe
    // to call unconditionally — keeps Sentry events filterable by user.
    Sentry.setUser({ id: user.id, username: user.username })
}

/** Emit `$pageview` on every SPA navigation; no-op when analytics is off. */
export function trackPageviews(router: RouteEventSource) {
    if (!initialized) return
    trackRouteChanges(router, (event, props) => posthog.capture(event, props))
}

export function resetPostHog() {
    analyticsActor = null
    if (initialized) posthog.reset()
    Sentry.setUser(null)
}

export { posthog }
